import { Button } from '@mantine/core';
import { Form, Formik } from 'formik';
import { isEmpty, values } from 'lodash/fp';
import React, { FC } from 'react';
import { Col, Row } from 'reactstrap';

import { VirtualizedSelect } from '@portals/autoformik';
import { ModalBody, ModalFooter } from '@portals/core';
import { FieldType, StateType } from '@portals/types';
import { NetworkButton } from '@portals/ui';

import ConfigurationMissingMessage from './ConfigurationMissingMessage';
import { DeviceNameField } from './DeviceNameField';
import { useCloud2CloudFormData } from './hooks';
import { ModelField } from './ModelField';
import { FormComponentCommonProps } from './types';
import { VendorField } from './VendorField';

interface QSysFormFields {
  c2c_id: string;
  name: string;
  model: string;
}

const INITIAL_VALUES: QSysFormFields = {
  c2c_id: '',
  name: '',
  model: '',
};

const QSysForm: FC<FormComponentCommonProps> = ({
  selectedPartnerId,
  setSelectedPartnerId,
  onSubmit,
  handleClose,
  isSubmitDisabled,
}) => {
  const {
    integration,
    fetchIntegration,
    isLoading,
    options,
    showConfigurationMissingMessage,
  } = useCloud2CloudFormData({
    integrationName: 'q_sys',
    composeOptions: composeCoresOptions,
  });

  return (
    <Formik<QSysFormFields> initialValues={INITIAL_VALUES} onSubmit={onSubmit}>
      {({ values, handleChange, handleBlur, setFieldValue }) => (
        <Form>
          <ModalBody>
            <VendorField
              vendor={selectedPartnerId}
              setVendor={setSelectedPartnerId}
            />
            {showConfigurationMissingMessage ? (
              <ConfigurationMissingMessage
                integration={integration}
                message="Please configure the QSys integration before claiming devices."
                linkTo="/settings/integrations/monitoring/q_sys"
                onLinkClick={handleClose}
              />
            ) : (
              <>
                <ModelField
                  partnerId={selectedPartnerId}
                  fieldName="model"
                  value={values.model}
                  setFieldValue={setFieldValue}
                />
                <Row className="align-items-end">
                  <Col sm={10}>
                    {isEmpty(options) ? (
                      <span className="mr-5 pt-1">No cores found</span>
                    ) : (
                      <VirtualizedSelect
                        field={{
                          title: 'Cores',
                          name: 'c2c_id',
                          placeholder: 'Select core',
                          options,
                          required: true,
                        }}
                        disabled={isLoading}
                        readOnly={false}
                        value={values.c2c_id}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        handleBlur={handleBlur}
                      />
                    )}
                  </Col>

                  <Col sm={2}>
                    <NetworkButton
                      type="button"
                      color="primary"
                      onClick={() => fetchIntegration({ type: 'q_sys' })}
                      request="reloadIntegrationq_sys"
                    >
                      Refresh
                    </NetworkButton>
                  </Col>
                </Row>
                <DeviceNameField
                  value={values.name}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                />
              </>
            )}
          </ModalBody>
          <ModalFooter position="right">
            <Button type="button" variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitDisabled || isLoading || !values.c2c_id || !values.model
              }
            >
              Claim device
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
};

function composeCoresOptions(
  integration: StateType['data']['integrations']['q_sys']
): FieldType['options'] {
  const cores = integration?.extra?.cores;

  if (!cores) {
    return {};
  }

  return values(cores).reduce((acc, core) => {
    acc[core.id] = core.name;
    return acc;
  }, {});
}

export default QSysForm;
